import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import {
  Bell,
  Settings,
  LogOut,
  Menu,
  X,
  CheckCircle,
  AlertCircle,
  MessageCircle,
  ChevronDown,
} from "lucide-react";
import { useAuth } from "../../App";
import { useTheme } from "../../contexts/ThemeContext";
import { useApp } from "../../contexts/AppContext";
import { useTranslation } from "react-i18next";

const Header = ({ toggleSidebar, sidebarOpen, isMobileMenuOpen = false }) => {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const userMenuRef = useRef(null);
  const notificationsRef = useRef(null);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
      if (
        notificationsRef.current &&
        !notificationsRef.current.contains(event.target)
      ) {
        setShowNotifications(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  const { notifications } = useApp();
  const [headerNotifications, setHeaderNotifications] = useState([]);

  useEffect(() => {
    setHeaderNotifications(notifications.slice(0, 3));
  }, [notifications]);

  const getIconForType = (type) => {
    switch (type) {
      case "session_created":
        return CheckCircle;
      case "payment":
        return AlertCircle;
      case "new_student":
        return CheckCircle;
      case "student_package_update":
        return CheckCircle;
      default:
        return MessageCircle;
    }
  };

  const getNotificationColor = (type) => {
    switch (type) {
      case "session_created":
        return "text-sky-400";
      case "payment":
        return "text-emerald-400";
      case "new_student":
        return "text-violet-400";
      default:
        return "text-slate-400";
    }
  };

  const getPageTitle = () => {
    const titles = {
      "/sessions": t("header.titles.sessions"),
      "/organizations": t("header.titles.organizations"),
      "/trainers": t("header.titles.trainers"),
      "/students": t("header.titles.students"),
      "/analytics": t("header.titles.analytics"),
      "/exercises": t("header.titles.exercises"),
      "/finance": t("header.titles.finance"),
      "/permissions": t("header.titles.permissions"),
      "/settings": t("header.titles.settings"),
    };
    return titles[location.pathname] || t("header.titles.dashboard");
  };

  const getUserInfo = () => {
    if (user) {
      let role = "student";
      try {
        if (user.session) {
          const idToken = user.session.getIdToken();
          const payload = idToken.payload;
          const groups = payload["cognito:groups"] || [];
          if (groups.includes("SuperAdmins")) role = "super_admin";
          else if (groups.includes("OrganizationManager"))
            role = "organization_manager";
          else if (groups.includes("Trainers")) role = "trainer";
          else if (groups.includes("Students")) role = "student";
        }
      } catch (error) {
        console.error("Error getting role:", error);
      }

      // Priority: name -> given_name + family_name -> email -> username
      const displayName =
        user.name ||
        (user.given_name ? `${user.given_name} ${user.family_name || ""}` : "") ||
        user.email ||
        user.username ||
        "User";

      return {
        name: displayName,
        email: user.email || "",
        username: user.username || "",
        role: role,
        initials: getInitials(displayName),
      };
    }
    return { name: "User", email: "", role: "student", initials: "U" };
  };

  const getInitials = (name) => {
    if (!name) return "U";
    const names = name.split(" ");
    return names.length >= 2
      ? `${names[0].charAt(0)}${names[1].charAt(0)}`.toUpperCase()
      : name.charAt(0).toUpperCase();
  };

  const getRoleBadgeColor = (role) => {
    const map = {
      super_admin: "role-badge-admin",
      organization_manager: "role-badge-manager",
      trainer: "role-badge-trainer",
      student: "role-badge-student",
    };
    return map[role] || "role-badge-default";
  };

  const getRoleLabel = (role) => {
    const map = {
      super_admin: "Super Admin",
      organization_manager: "Org. Manager",
      trainer: "Trainer",
      student: "Student",
    };
    return map[role] || "User";
  };

  const handleLogout = () => {
    logout();
    setShowUserMenu(false);
    navigate("/login");
  };

  const userInfo = getUserInfo();
  const unreadCount = notifications.filter(n => !n.readBy?.includes(userInfo?.name)).length;

  return (
    <header
      className={`
      mx-3 mt-3 mb-2 sm:mx-4 sm:mt-4
      ${colors.bgSecondary} ${colors.border}
      header-container
    `}
    >
      <div className="px-3 py-3 sm:px-6 sm:py-4 flex items-center justify-between gap-2">
        {/* Left: Toggle & Title */}
        <div className="flex items-center space-x-2 sm:space-x-5 min-w-0">
          <button
            onClick={toggleSidebar}
            aria-label="Menüyü aç/kapat"
            className={`
              header-icon-btn group shrink-0 ${colors.textSecondary} hover:text-white ${colors.bgHover}
            `}
          >
            {(isMobileMenuOpen || sidebarOpen) ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>

          <h1
            className={`
            text-lg sm:text-2xl font-black tracking-tight truncate min-w-0 ${colors.textPrimary}
          `}
          >
            {getPageTitle()}
          </h1>
        </div>

        {/* Right: Actions */}
        <div className="flex items-center space-x-2 sm:space-x-4 shrink-0">
          {/* Notifications */}
          <div className="relative" ref={notificationsRef}>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowNotifications(!showNotifications);
              }}
              className={`
                header-icon-btn group ${colors.textSecondary} hover:text-white ${colors.bgHover}
              `}
            >
              <Bell className="w-6 h-6 group-hover:rotate-12 transition-transform" />
              {unreadCount > 0 && (
                <span className="absolute top-1 right-1 w-4 h-4 bg-rose-500 rounded-full shadow-glow animate-pulse flex items-center justify-center">
                  <span className="text-[10px] text-white font-bold">{unreadCount}</span>
                </span>
              )}
            </button>

            {showNotifications && (
              <div
                className={`
                fixed inset-x-3 top-20 sm:absolute sm:inset-x-auto sm:top-auto sm:right-0 sm:mt-4 sm:w-96
                ${colors.cardBg} ${colors.cardBorder}
                rounded-3xl shadow-2xl border
                animate-in slide-in-from-top-4 fade-in duration-300
                backdrop-blur-xl overflow-hidden z-50
              `}
              >
                <div
                  className={`p-5 border-b flex justify-between items-center ${colors.border} ${colors.bgTertiary}`}
                >
                  <h3 className={`font-bold text-lg ${colors.textPrimary}`}>
                    {t("header.notifications")}
                  </h3>
                  <Link 
                    to="/notifications" 
                    className={`text-sm ${colors.textTertiary} hover:${colors.textPrimary} font-medium`}
                    onClick={() => setShowNotifications(false)}
                  >
                    View All
                  </Link>
                </div>
                <div className="p-2 space-y-1">
                  {headerNotifications.length > 0 ? (
                    headerNotifications.map((notif) => {
                      const Icon = getIconForType(notif.type);
                      return (
                        <button
                          key={notif.id}
                          className={`
                        w-full flex items-start space-x-4 p-4 rounded-2xl text-left
                        ${colors.bgHover} transition-all duration-200
                      `}
                        >
                          <div
                            className={`p-2 rounded-xl ${colors.bgTertiary} ${getNotificationColor(notif.type)}`}
                          >
                            <Icon className="w-5 h-5" />
                          </div>
                          <div className="flex-1">
                            <p
                              className={`text-sm font-bold ${colors.textPrimary}`}
                            >
                              {notif.title}
                            </p>
                            <p
                              className={`text-xs ${colors.textSecondary} mt-1 line-clamp-2`}
                            >
                              {notif.body}
                            </p>
                          </div>
                        </button>
                      );
                    })
                  ) : (
                    <div className={`p-8 text-center ${colors.textMuted}`}>
                      <Bell className="w-8 h-8 mx-auto mb-2 opacity-20" />
                      <p className="text-sm">{t("header.noNotifications")}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* User Profile */}
          <div className="relative" ref={userMenuRef}>
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className={`
                flex items-center space-x-3 pl-2 pr-4 py-2
                rounded-[2rem] ${colors.bgHover} border border-transparent 
                hover:border-slate-200 dark:hover:border-white/10
                transition-all duration-300 hover:shadow-lg
              `}
            >
              <div
                className={`
                user-avatar-badge ${getRoleBadgeColor(userInfo.role)}
              `}
              >
                <span className="text-white font-bold text-sm">
                  {userInfo.initials}
                </span>
              </div>
              <div className="hidden md:block text-left">
                <p className={`text-sm font-bold ${colors.textPrimary}`}>
                  {userInfo.name || "User"}
                </p>
                <p
                  className={`text-xs ${colors.textSecondary} font-medium capitalize`}
                >
                  {getRoleLabel(userInfo.role)}
                </p>
              </div>
              <ChevronDown
                className={`w-4 h-4 ${colors.textTertiary} hidden md:block`}
              />
            </button>

            {showUserMenu && (
              <div
                className={`
                absolute right-0 mt-4 w-72
                ${colors.cardBg} ${colors.cardBorder}
                rounded-3xl shadow-2xl border
                animate-in slide-in-from-top-4 fade-in duration-300
                backdrop-blur-xl overflow-hidden
              `}
              >
                <div
                  className={`p-6 text-center border-b ${colors.border} ${colors.bgTertiary}`}
                >
                  <div
                    className={`
                    user-avatar-badge-lg mx-auto mb-3 ${getRoleBadgeColor(userInfo.role)}
                  `}
                  >
                    <span className="text-white font-bold text-2xl">
                      {userInfo.initials}
                    </span>
                  </div>
                  <h3 className={`font-bold text-lg ${colors.textPrimary}`}>
                    {userInfo.name}
                  </h3>
                  <p className={`text-sm ${colors.textSecondary}`}>
                    {userInfo.email}
                  </p>
                </div>

                <div className="p-2">
                  <button
                    onClick={() => {
                      navigate("/settings");
                      setShowUserMenu(false);
                    }}
                    className={`
                    w-full flex items-center space-x-3 px-4 py-3 rounded-2xl
                    ${colors.textSecondary} ${colors.bgHover} hover:text-slate-900 dark:hover:text-white
                    transition-all font-medium
                  `}
                  >
                    <Settings className="w-5 h-5" />
                    <span>{t("header.settings")}</span>
                  </button>

                  <div
                    className={`h-px bg-gradient-to-r from-transparent via-slate-200 dark:via-white/10 to-transparent my-1`}
                  />

                  <button
                    onClick={handleLogout}
                    className="
                    w-full flex items-center space-x-3 px-4 py-3 rounded-2xl
                    text-rose-500 hover:text-rose-600 dark:text-rose-400 dark:hover:text-white dark:hover:bg-rose-500/20
                    transition-all font-medium
                  "
                  >
                    <LogOut className="w-5 h-5" />
                    <span>{t("header.signOut")}</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
